import { useState, useEffect } from "react";

const STORAGE_KEY = "lhda_orgchart_7";
const defaultData = { wings: [] };

function genId() {
  return Math.random().toString(36).slice(2, 9);
}

/* ── Generic name input modal ── */
function NameModal({ title, placeholder, onConfirm, onCancel, children }) {
  const [name, setName] = useState("");
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>{title}</h3>
        {children}
        <input
          autoFocus
          style={S.modalInput}
          placeholder={placeholder}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim()) onConfirm(name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && onConfirm(name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Toolbar modal: Add Division (pick wing first) ── */
function AddDivisionModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const [name, setName] = useState("");
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>Add Division</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Division Name</label>
        <input
          autoFocus
          style={S.modalInput}
          placeholder="Division name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim() && wingId) onConfirm(wingId, name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && wingId && onConfirm(wingId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Toolbar modal: Add Branch (pick wing → division) ── */
function AddBranchModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const wing = wings.find(w => w.id === wingId);
  const [divId, setDivId] = useState(wing?.divisions[0]?.id || "");
  const [name, setName] = useState("");

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    setDivId(w?.divisions[0]?.id || "");
  }, [wingId, wings]);

  const divs = wing?.divisions || [];
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>Add Branch</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Select Division</label>
        <select style={S.select} value={divId} onChange={e => setDivId(e.target.value)} disabled={!divs.length}>
          {divs.length ? divs.map(d => <option key={d.id} value={d.id}>{d.name}</option>) : <option>— no divisions yet —</option>}
        </select>
        <label style={S.label}>Branch Name</label>
        <input
          autoFocus
          style={S.modalInput}
          placeholder="Branch name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim() && divId) onConfirm(wingId, divId, name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && divId && onConfirm(wingId, divId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Toolbar modal: Add Unit (pick wing → division → branch) ── */
function AddUnitModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const wing = wings.find(w => w.id === wingId);
  const [divId, setDivId] = useState(wing?.divisions[0]?.id || "");
  const div = wing?.divisions.find(d => d.id === divId);
  const [branchId, setBranchId] = useState(div?.branches[0]?.id || "");
  const [name, setName] = useState("");

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    const firstDiv = w?.divisions[0];
    setDivId(firstDiv?.id || "");
    setBranchId(firstDiv?.branches[0]?.id || "");
  }, [wingId, wings]);

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    const d = w?.divisions.find(d => d.id === divId);
    setBranchId(d?.branches[0]?.id || "");
  }, [divId, wingId, wings]);

  const divs = wing?.divisions || [];
  const branches = div?.branches || [];

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>Add Unit</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Select Division</label>
        <select style={S.select} value={divId} onChange={e => setDivId(e.target.value)} disabled={!divs.length}>
          {divs.length ? divs.map(d => <option key={d.id} value={d.id}>{d.name}</option>) : <option>— no divisions yet —</option>}
        </select>
        <label style={S.label}>Select Branch</label>
        <select style={S.select} value={branchId} onChange={e => setBranchId(e.target.value)} disabled={!branches.length}>
          {branches.length ? branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>) : <option>— no branches yet —</option>}
        </select>
        <label style={S.label}>Unit Name</label>
        <input
          autoFocus
          style={S.modalInput}
          placeholder="Unit name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim() && branchId) onConfirm(wingId, divId, branchId, name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && branchId && onConfirm(wingId, divId, branchId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Employee card ── */
function EmployeeCard({ emp, onDelete }) {
  return (
    <div style={S.empCard}>
      <div style={S.empAvatar}>{emp.name[0].toUpperCase()}</div>
      <div style={S.empName}>{emp.name}</div>
      <div style={S.empPos}>{emp.position || "Employee"}</div>
      <button style={S.empDel} onClick={onDelete} title="Remove">✕</button>
    </div>
  );
}

/* ── Unit node ── */
function UnitNode({ unit, onDelete, onAddEmployee, onDeleteEmployee }) {
  const [modal, setModal] = useState(false);
  return (
    <div style={S.unitBox}>
      <div style={S.unitHeader}>
        <span style={S.badge("#6b3fa0")}>UNIT</span>
        <span style={S.nodeName}>{unit.name}</span>
        <button style={S.delBtn} onClick={onDelete} title="Remove unit">✕</button>
      </div>
      <div style={S.empRow}>
        {unit.employees.map(emp => (
          <EmployeeCard key={emp.id} emp={emp} onDelete={() => onDeleteEmployee(emp.id)} />
        ))}
        <button style={S.addEmpBtn} onClick={() => setModal(true)}>+ Employee</button>
      </div>
      {modal && (
        <NameModal
          title="Add Employee"
          placeholder="Employee name"
          onConfirm={name => { onAddEmployee(name); setModal(false); }}
          onCancel={() => setModal(false)}
        />
      )}
    </div>
  );
}

/* ── Branch node ── */
function BranchNode({ branch, onDelete, onAddUnit, onDeleteUnit, onAddEmployee, onDeleteEmployee }) {
  const [modal, setModal] = useState(false);
  return (
    <div style={S.branchBox}>
      <div style={S.branchHeader}>
        <span style={S.badge("#b07d2e")}>BRANCH</span>
        <span style={S.nodeName}>{branch.name}</span>
        <button style={S.addSmallBtn} onClick={() => setModal(true)}>+ Unit</button>
        <button style={S.delBtn} onClick={onDelete} title="Remove branch">✕</button>
      </div>
      <div style={S.children}>
        {branch.units.map(unit => (
          <UnitNode
            key={unit.id}
            unit={unit}
            onDelete={() => onDeleteUnit(unit.id)}
            onAddEmployee={name => onAddEmployee(unit.id, name)}
            onDeleteEmployee={eid => onDeleteEmployee(unit.id, eid)}
          />
        ))}
      </div>
      {modal && (
        <NameModal
          title="Add Unit"
          placeholder="Unit name"
          onConfirm={name => { onAddUnit(name); setModal(false); }}
          onCancel={() => setModal(false)}
        />
      )}
    </div>
  );
}

/* ── Division node ── */
function DivisionNode({ division, onDelete, onAddBranch, onDeleteBranch, onAddUnit, onDeleteUnit, onAddEmployee, onDeleteEmployee }) {
  const [modal, setModal] = useState(false);
  return (
    <div style={S.divisionBox}>
      <div style={S.divisionHeader}>
        <span style={S.badge("#2878b5")}>DIVISION</span>
        <span style={S.nodeName}>{division.name}</span>
        <button style={S.addSmallBtn} onClick={() => setModal(true)}>+ Branch</button>
        <button style={S.delBtn} onClick={onDelete} title="Remove division">✕</button>
      </div>
      <div style={S.children}>
        {division.branches.map(branch => (
          <BranchNode
            key={branch.id}
            branch={branch}
            onDelete={() => onDeleteBranch(branch.id)}
            onAddUnit={name => onAddUnit(branch.id, name)}
            onDeleteUnit={uid => onDeleteUnit(branch.id, uid)}
            onAddEmployee={(uid, name) => onAddEmployee(branch.id, uid, name)}
            onDeleteEmployee={(uid, eid) => onDeleteEmployee(branch.id, uid, eid)}
          />
        ))}
      </div>
      {modal && (
        <NameModal
          title="Add Branch"
          placeholder="Branch name"
          onConfirm={name => { onAddBranch(name); setModal(false); }}
          onCancel={() => setModal(false)}
        />
      )}
    </div>
  );
}

/* ── Wing node ── */
function WingNode({ wing, onDelete, onAddDivision, onDeleteDivision, onAddBranch, onDeleteBranch, onAddUnit, onDeleteUnit, onAddEmployee, onDeleteEmployee }) {
  const [modal, setModal] = useState(false);
  return (
    <div style={S.wingBox}>
      <div style={S.wingHeader}>
        <span style={S.badge("#1a4d28")}>WING</span>
        <span style={S.wingName}>{wing.name}</span>
        <button style={S.addSmallBtn} onClick={() => setModal(true)}>+ Division</button>
        <button style={S.delBtn} onClick={onDelete} title="Remove wing">✕</button>
      </div>
      <div style={S.children}>
        {wing.divisions.map(div => (
          <DivisionNode
            key={div.id}
            division={div}
            onDelete={() => onDeleteDivision(div.id)}
            onAddBranch={name => onAddBranch(div.id, name)}
            onDeleteBranch={bid => onDeleteBranch(div.id, bid)}
            onAddUnit={(bid, name) => onAddUnit(div.id, bid, name)}
            onDeleteUnit={(bid, uid) => onDeleteUnit(div.id, bid, uid)}
            onAddEmployee={(bid, uid, name) => onAddEmployee(div.id, bid, uid, name)}
            onDeleteEmployee={(bid, uid, eid) => onDeleteEmployee(div.id, bid, uid, eid)}
          />
        ))}
      </div>
      {modal && (
        <NameModal
          title="Add Division"
          placeholder="Division name"
          onConfirm={name => { onAddDivision(name); setModal(false); }}
          onCancel={() => setModal(false)}
        />
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function OrgChartPage() {
  const [data, setData] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || defaultData; } catch { return defaultData; }
  });
  const [toolbar, setToolbar] = useState(null); // "wing" | "division" | "branch" | "unit"

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  /* ── state mutators ── */
  const addWing = name =>
    setData(d => ({ wings: [...d.wings, { id: genId(), name, divisions: [] }] }));

  const deleteWing = wid =>
    setData(d => ({ wings: d.wings.filter(w => w.id !== wid) }));

  const addDivision = (wid, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: [...w.divisions, { id: genId(), name, branches: [] }] }) }));

  const deleteDivision = (wid, did) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.filter(dv => dv.id !== did) }) }));

  const addBranch = (wid, did, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: [...dv.branches, { id: genId(), name, units: [] }] }) }) }));

  const deleteBranch = (wid, did, bid) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.filter(b => b.id !== bid) }) }) }));

  const addUnit = (wid, did, bid, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: [...b.units, { id: genId(), name, employees: [] }] }) }) }) }));

  const deleteUnit = (wid, did, bid, uid) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: b.units.filter(u => u.id !== uid) }) }) }) }));

  const addEmployee = (wid, did, bid, uid, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: b.units.map(u => u.id !== uid ? u : { ...u, employees: [...u.employees, { id: genId(), name, position: "Employee" }] }) }) }) }) }));

  const deleteEmployee = (wid, did, bid, uid, eid) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: b.units.map(u => u.id !== uid ? u : { ...u, employees: u.employees.filter(e => e.id !== eid) }) }) }) }) }));

  const noWings = data.wings.length === 0;
  const noDivisions = data.wings.every(w => w.divisions.length === 0);
  const noBranches = data.wings.every(w => w.divisions.every(d => d.branches.length === 0));

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Organizational Chart</h1>
        <p style={S.subtitle}>Non-Teachers — Unit 7</p>
      </div>

      {/* ── Toolbar ── */}
      <div style={S.toolbar}>
        <button style={S.btnWing}   onClick={() => setToolbar("wing")}>+ Add Wing</button>
        <button style={{...S.btnDiv,    opacity: noWings ? .5 : 1}} onClick={() => !noWings && setToolbar("division")}>+ Add Division</button>
        <button style={{...S.btnBranch, opacity: noDivisions ? .5 : 1}} onClick={() => !noDivisions && setToolbar("branch")}>+ Add Branch</button>
        <button style={{...S.btnUnit,   opacity: noBranches ? .5 : 1}} onClick={() => !noBranches && setToolbar("unit")}>+ Add Unit</button>
      </div>

      {/* ── Legend ── */}
      <div style={S.legend}>
        {[["#1a4d28","Wing"],["#2878b5","Division"],["#b07d2e","Branch"],["#6b3fa0","Unit"],["#c0392b","Employee"]].map(([color, label]) => (
          <span key={label} style={S.legendItem}><span style={{...S.dot, background: color}}/>{label}</span>
        ))}
      </div>

      {/* ── Chart ── */}
      <div style={S.chart}>
        {noWings && <div style={S.empty}>No structure yet. Click <strong>+ Add Wing</strong> to begin.</div>}
        {data.wings.map(wing => (
          <WingNode
            key={wing.id}
            wing={wing}
            onDelete={() => deleteWing(wing.id)}
            onAddDivision={name => addDivision(wing.id, name)}
            onDeleteDivision={did => deleteDivision(wing.id, did)}
            onAddBranch={(did, name) => addBranch(wing.id, did, name)}
            onDeleteBranch={(did, bid) => deleteBranch(wing.id, did, bid)}
            onAddUnit={(did, bid, name) => addUnit(wing.id, did, bid, name)}
            onDeleteUnit={(did, bid, uid) => deleteUnit(wing.id, did, bid, uid)}
            onAddEmployee={(did, bid, uid, name) => addEmployee(wing.id, did, bid, uid, name)}
            onDeleteEmployee={(did, bid, uid, eid) => deleteEmployee(wing.id, did, bid, uid, eid)}
          />
        ))}
      </div>

      {/* ── Toolbar modals ── */}
      {toolbar === "wing" && (
        <NameModal title="Add Wing" placeholder="Wing name" onConfirm={name => { addWing(name); setToolbar(null); }} onCancel={() => setToolbar(null)} />
      )}
      {toolbar === "division" && (
        <AddDivisionModal wings={data.wings} onConfirm={(wid, name) => { addDivision(wid, name); setToolbar(null); }} onCancel={() => setToolbar(null)} />
      )}
      {toolbar === "branch" && (
        <AddBranchModal wings={data.wings} onConfirm={(wid, did, name) => { addBranch(wid, did, name); setToolbar(null); }} onCancel={() => setToolbar(null)} />
      )}
      {toolbar === "unit" && (
        <AddUnitModal wings={data.wings} onConfirm={(wid, did, bid, name) => { addUnit(wid, did, bid, name); setToolbar(null); }} onCancel={() => setToolbar(null)} />
      )}
    </div>
  );
}

/* ── Styles ── */
const S = {
  page: { minHeight: "100vh", background: "#f5f9f6", paddingBottom: "4rem" },
  header: { background: "linear-gradient(135deg,#1a4d28,#2878b5)", color: "#fff", padding: "3rem 2rem 2rem", textAlign: "center" },
  title: { fontFamily: "'Playfair Display',serif", fontSize: "2.2rem", marginBottom: ".4rem" },
  subtitle: { opacity: .8, fontSize: "1rem" },

  toolbar: { display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center", padding: "1.5rem 2rem 0" },
  btnWing:   { padding: ".65rem 1.5rem", borderRadius: 8, border: "none", background: "#1a4d28", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnDiv:    { padding: ".65rem 1.5rem", borderRadius: 8, border: "none", background: "#2878b5", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnBranch: { padding: ".65rem 1.5rem", borderRadius: 8, border: "none", background: "#b07d2e", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnUnit:   { padding: ".65rem 1.5rem", borderRadius: 8, border: "none", background: "#6b3fa0", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },

  legend: { display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center", padding: "1rem 2rem .5rem", fontSize: ".82rem", color: "#4e6058" },
  legendItem: { display: "flex", alignItems: "center", gap: ".35rem" },
  dot: { width: 10, height: 10, borderRadius: "50%", display: "inline-block" },

  chart: { padding: "1.5rem 2rem", maxWidth: 1400, margin: "0 auto" },
  empty: { textAlign: "center", color: "#4e6058", padding: "4rem 0", fontSize: "1rem" },

  wingBox: { border: "2.5px solid #1a4d28", borderRadius: 12, marginBottom: "1.5rem", background: "#fff", boxShadow: "0 4px 20px rgba(13,27,15,.10)" },
  wingHeader: { background: "#1a4d28", borderRadius: "9px 9px 0 0", padding: ".75rem 1rem", display: "flex", alignItems: "center", gap: ".75rem", flexWrap: "wrap" },
  wingName: { color: "#fff", fontWeight: 800, fontSize: "1.05rem", flex: 1 },

  divisionBox: { border: "2px solid #2878b5", borderRadius: 10, margin: "1rem", background: "#f0f6fb" },
  divisionHeader: { background: "#2878b5", borderRadius: "8px 8px 0 0", padding: ".6rem 1rem", display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" },

  branchBox: { border: "2px solid #b07d2e", borderRadius: 8, margin: "1rem", background: "#fdf8ef" },
  branchHeader: { background: "#b07d2e", borderRadius: "6px 6px 0 0", padding: ".5rem .9rem", display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" },

  unitBox: { border: "2px solid #6b3fa0", borderRadius: 8, margin: "1rem", background: "#f7f2fd" },
  unitHeader: { background: "#6b3fa0", borderRadius: "6px 6px 0 0", padding: ".5rem .9rem", display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" },

  children: { padding: ".5rem" },
  nodeName: { color: "#fff", fontWeight: 700, flex: 1 },

  badge: color => ({ fontSize: ".65rem", fontWeight: 800, letterSpacing: ".08em", padding: ".15rem .45rem", borderRadius: 4, background: "rgba(255,255,255,.22)", color: "#fff" }),
  addSmallBtn: { padding: ".25rem .7rem", fontSize: ".78rem", borderRadius: 6, border: "1.5px solid rgba(255,255,255,.6)", background: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 700, cursor: "pointer" },
  delBtn: { padding: ".2rem .5rem", fontSize: ".75rem", borderRadius: 5, border: "none", background: "rgba(255,255,255,.18)", color: "#fff", fontWeight: 700, lineHeight: 1, cursor: "pointer" },

  empRow: { display: "flex", flexWrap: "wrap", gap: ".75rem", padding: ".75rem" },
  empCard: { position: "relative", background: "#fff", border: "1.5px solid #d4c4ef", borderRadius: 8, padding: ".6rem .8rem", textAlign: "center", minWidth: 90, maxWidth: 110 },
  empAvatar: { width: 36, height: 36, borderRadius: "50%", background: "#c0392b", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1rem", margin: "0 auto .4rem" },
  empName: { fontSize: ".78rem", fontWeight: 700, color: "#1a4d28", wordBreak: "break-word" },
  empPos: { fontSize: ".68rem", color: "#4e6058", marginTop: ".15rem" },
  empDel: { position: "absolute", top: 4, right: 4, padding: ".1rem .35rem", fontSize: ".65rem", borderRadius: 4, border: "none", background: "#f5e6e6", color: "#c0392b", fontWeight: 700, cursor: "pointer" },
  addEmpBtn: { padding: ".35rem .7rem", fontSize: ".78rem", borderRadius: 6, border: "1.5px dashed #6b3fa0", background: "transparent", color: "#6b3fa0", fontWeight: 700, cursor: "pointer", alignSelf: "center" },

  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 12, padding: "2rem", width: "min(92vw,420px)", boxShadow: "0 14px 52px rgba(0,0,0,.25)" },
  modalTitle: { fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", marginBottom: "1.2rem", color: "#1a4d28" },
  label: { display: "block", fontSize: ".8rem", fontWeight: 700, color: "#4e6058", marginBottom: ".35rem" },
  select: { width: "100%", padding: ".6rem .9rem", borderRadius: 7, border: "1.5px solid #ccddd2", fontSize: ".95rem", marginBottom: "1rem", background: "#fff" },
  modalInput: { width: "100%", padding: ".7rem 1rem", borderRadius: 8, border: "1.5px solid #ccddd2", fontSize: "1rem", marginBottom: "1.2rem", outline: "none" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: ".75rem" },
  btnCancel: { padding: ".55rem 1.2rem", borderRadius: 7, border: "1.5px solid #ccc", background: "#fff", color: "#4e6058", fontWeight: 600, cursor: "pointer" },
  btnPrimary: { padding: ".55rem 1.4rem", borderRadius: 7, border: "none", background: "#1a4d28", color: "#fff", fontWeight: 700, cursor: "pointer" },
};
